import { useEffect, useRef, useState, useCallback } from "react";
import {
  Route,
  HashRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { invalidateLanguageCache } from "./api/cacheUtils";
import { getStayInfo } from "./api/stayService";
import { getPromotionCode } from "./api/promotionService";
import "./App.css";
import CartReplaceConfirm from "./components/CartReplaceConfirm";
import CategorySection from "./components/CategorySection";
import CurationSection from "./components/CurationSection";
import Footer from "./components/Footer";
import Header from "./components/Header";
import HomeRecentOrderAffordance from "./components/HomeRecentOrderAffordance";
import LocationBanner from "./components/LocationBanner";
import NavigationBar from "./components/NavigationBar";
import OperationTimeGuard from "./components/OperationTimeGuard";
import ServiceClosingGuard from "./components/ServiceClosingGuard";
import GuideBanner from "./components/GuideBanner";
import BottomBanner from "./components/BottomBanner";
import FigmaMcpCaptureRunner from "./components/FigmaMcpCaptureRunner";
import MaintenancePage from "./components/MaintenancePage";
import {
  getCurrentLanguage,
  LANGUAGE_CHANGE_EVENT,
  SupportedLanguage,
  setLanguage,
} from "./config/languageConfig";
import { getTranslation } from "./config/translations";
import { AddressProvider, useAddress } from "./contexts/AddressContext";
import { CartProvider } from "./contexts/CartContext";
import { AddressAffordanceProvider, useAddressAffordance } from "./contexts/AddressAffordanceContext";
import { NavigationAffordanceProvider } from "./contexts/NavigationAffordanceContext";
import AddressSetupPage from "./pages/AddressSetupPage";
import AddressMethodSelection from "./pages/address/AddressMethodSelection";
import AddressScreenshotUpload from "./pages/address/AddressScreenshotUpload";
import CartPage from "./pages/CartPage";
import CategoryDetail from "./pages/CategoryDetail";
import CurationDetail from "./pages/CurationDetail";
import CheckoutPage from "./pages/CheckoutPage";
import MenuDetail from "./pages/MenuDetail";
import ProductDetail from "./pages/ProductDetail";
import NotFoundPage from "./pages/NotFoundPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import OrdersPage from "./pages/OrdersPage";
import PaymentCallbackPage from "./pages/PaymentCallbackPage";
import RestaurantDetail from "./pages/RestaurantDetail";
import GiftShopDetail from "./pages/GiftShopDetail";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingPage2 from "./pages/OnboardingPage2";
import OnboardingPage3 from "./pages/OnboardingPage3";
import OnboardingPage4 from "./pages/OnboardingPage4";
import FAQPage from "./pages/FAQPage";
import FAQSectionPage from "./pages/FAQSectionPage";
import { trackPageView } from "./services/AnalyticsService";
import { getOperationStatus } from "./services/OperationTimeService";
import {
  bootChannelTalk,
  setChannelTalkPage,
  shutdownChannelTalk,
} from "./services/ChannelService";
import GuidePage from "./pages/GuidePage";
import GuideDetailPage from "./pages/GuideDetailPage";
import SearchResultPage from "./pages/SearchResultPage";
import SearchPage from "./pages/SearchPage";
import {
  transformPromotionData,
  canShowPromotionPopup,
} from "./utils/promotionUtils";
import { PromotionPopupData } from "./types/promotion";
import PromotionCodePage from "./pages/PromotionCodePage";
import { useStayPreloader } from './hooks/useStayPreloader';
import { useHomeDataPreloader } from './hooks/useHomeDataPreloader';
import { pollingService, StayInfo } from './services/PollingService';
import { savePendingStayId, loadPendingStayId, removePendingStayId } from './utils/pendingStayUtils';
import { getPromotionUserId, savePromotionUserId } from './utils/promotionUserIdUtils';

// 백그라운드 Polling 관리 컴포넌트
const BackgroundPollingManager = () => {
  const { setAddress } = useAddress();
  const { showAddressSetSuccessAffordance } = useAddressAffordance();

  useEffect(() => {
    // 백그라운드 polling 성공 콜백 정의
    const handlePollingSuccess = (stayInfo: StayInfo) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[BackgroundPollingManager] Setting address from polling result:', stayInfo);
      }

      // 별도로 저장된 selectedMethod 확인
      let selectedMethod: 'gps' | 'manual' | 'screenshot' = 'screenshot';
      try {
        const storedMethod = localStorage.getItem('selectedAddressMethod');
        if (storedMethod === 'gps' || storedMethod === 'manual' || storedMethod === 'screenshot') {
          selectedMethod = storedMethod;
        }
      } catch (error) {
        console.error('Error loading selectedAddressMethod:', error);
      }

      // 기존 stay 설정과 동일한 방식으로 주소 정보 설정
      const newAddress = {
        mainAddress: stayInfo.address.address,
        detailAddress: stayInfo.address.detail_address,
        isComplete: true,
        lat: stayInfo.address.lat,
        lng: stayInfo.address.lng,
        addressId: stayInfo.address.id,
        stay: stayInfo, // stay 정보 포함
        selectedMethod: selectedMethod
      };

      setAddress(newAddress);
      
      // 별도로 저장된 selectedMethod 정리
      try {
        localStorage.removeItem('selectedAddressMethod');
      } catch (error) {
        console.error('Error removing selectedAddressMethod:', error);
      }
      
      // 주소 설정 성공 어포던스 트리거
      setTimeout(() => {
        showAddressSetSuccessAffordance();
      }, 500); // 주소 설정 후 약간의 지연을 두고 어포던스 표시
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[BackgroundPollingManager] Address updated successfully');
      }
    };

    // 글로벌 성공 콜백 등록
    pollingService.setGlobalSuccessCallback(handlePollingSuccess);

    // 컴포넌트 언마운트 시 polling 정리
    return () => {
      if (pollingService.isCurrentlyPolling()) {
        pollingService.stopPolling();
      }
      // 콜백 정리
      pollingService.setGlobalSuccessCallback(() => {});
    };
  }, [setAddress, showAddressSetSuccessAffordance]);

  return null; // UI를 렌더링하지 않음
};

// Google Analytics 페이지 추적 컴포넌트
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // 현재 경로 추출
    const currentPath = window.location.hash.replace("#", "") || "/";

    // Google Analytics에 페이지 뷰 이벤트 전송
    trackPageView(currentPath);
  }, [location.pathname, location.hash]);

  return null;
};

// 채널톡 관리 컴포넌트 - 홈 페이지에서만 활성화
const ChannelTalkManager = () => {
  const location = useLocation();

  // DOM에서 채널톡 버튼 요소들을 모두 찾아 표시/숨김 처리하는 함수
  const toggleChannelTalkButton = (show: boolean) => {
    // 커스텀 버튼 처리
    const customButton = document.getElementById("custom-channel-button");
    if (customButton) {
      customButton.style.display = show ? "flex" : "none";
    }

    // 채널톡의 기본 버튼 처리 (클래스로 찾기)
    const channelButtons = document.querySelectorAll('[class*="channel-"]');
    channelButtons.forEach((button) => {
      const element = button as HTMLElement;
      if (
        element.tagName === "BUTTON" ||
        (element.tagName === "DIV" &&
          (element.style.position === "fixed" ||
            element.getAttribute("class")?.includes("launcher")))
      ) {
        // z-index 조정을 통해 다른 UI 요소와 겹치지 않도록 함
        if (element.style.zIndex) {
          element.style.zIndex = "998"; // 낮은 z-index 사용
        }
        element.style.display = show ? "" : "none";
      }
    });

    // iframe 처리 (채널톡은 종종 iframe을 사용)
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      if (iframe.src.includes("channel.io")) {
        // z-index 조정
        const parent = iframe.parentElement;
        if (parent && parent instanceof HTMLElement) {
          parent.style.zIndex = "998";
        }
        iframe.style.display = show ? "" : "none";
      }
    });
  };

  useEffect(() => {
    // 현재 언어 설정으로 채널톡 초기화
    const currentLang = getCurrentLanguage();
    bootChannelTalk(
      {
        language: currentLang,
      },
      true
    ); // 강제 초기화 플래그 true로 설정

    // 현재 페이지 이름 설정
    setChannelTalkPage(location.pathname || "Home");

    // 약간의 지연 후 DOM에서 버튼 표시
    setTimeout(() => {
      toggleChannelTalkButton(true);
    }, 500);

    // 컴포넌트 언마운트 시 정리 작업
    return () => {
      // 특별한 정리 작업은 필요 없음
    };
  }, [location.pathname, location.hash]);

  // 초기 로드 시 한 번만 실행
  useEffect(() => {
    // GitHub Pages에서는 초기 로드 시 추가 부트스트랩 시도
    if (window.location.hostname.includes("github.io")) {
      // 약간의 지연 후 채널톡 초기화 다시 시도
      setTimeout(() => {
        const currentLang = getCurrentLanguage();
        bootChannelTalk(
          {
            language: currentLang,
          },
          true
        );
      }, 1500);
    }
  }, []);

  // 언어 변경 감지 및 처리
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: SupportedLanguage }>;
      const newLang = customEvent.detail.language;

      // 홈 페이지에서만 언어 변경 적용
      if (window.ChannelIO) {
        shutdownChannelTalk();

        // 약간의 지연 후 새 언어로 재시작
        setTimeout(() => {
          bootChannelTalk(
            {
              language: newLang,
            },
            true
          );
        }, 300);
      }
    };

    // 언어 변경 이벤트 리스너 등록
    window.addEventListener(
      LANGUAGE_CHANGE_EVENT,
      handleLanguageChange as EventListener
    );

    return () => {
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange as EventListener
      );
    };
  }, []);

  return null;
};

// 글로벌 URL 파라미터 처리 컴포넌트
const GlobalUrlParameterHandler = () => {
  const location = useLocation();
  const { setAddress } = useAddress();
  const hasLoadedRef = useRef<string | null>(null);
  const [stayLoadError, setStayLoadError] = useState(false);
  const [failedStayId, setFailedStayId] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // stay 정보 로드 공통 로직
  const loadStayInfo = useCallback(async (stayId: string): Promise<boolean> => {
    try {
      console.log("📍 GlobalUrlParameterHandler: Stay 정보 로드 시작", stayId);
      const stayInfo = await getStayInfo(stayId);

      if (stayInfo.active === false) {
        removePendingStayId();
        hasLoadedRef.current = stayId;
        return false;
      }

      // 주소 정보를 AddressContext에 설정
      const newAddress = {
        mainAddress: stayInfo.address.address,
        detailAddress: stayInfo.address.detail_address,
        isComplete: true,
        lat: stayInfo.address.lat,
        lng: stayInfo.address.lng,
        addressId: stayInfo.address.id,
        stay: stayInfo  // stay 정보 추가
      };

      setAddress(newAddress);

      // API 성공 후에만 pendingStayId 제거
      removePendingStayId();
      hasLoadedRef.current = stayId;
      setStayLoadError(false);
      setFailedStayId(null);
      console.log("✅ GlobalUrlParameterHandler: 주소 설정 완료", stayInfo.address.address);
      return true;
    } catch (error) {
      console.error("❌ GlobalUrlParameterHandler: Stay 정보 로드 실패:", error);
      // 실패 시 pendingStayId 유지 → 재시도 가능
      // hasLoadedRef를 설정하지 않아 다음 location 변경 시 자동 재시도 가능
      setFailedStayId(stayId);
      setStayLoadError(true);

      // 토스트 5초 후 자동 숨김
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        setStayLoadError(false);
      }, 5000);

      return false;
    }
  }, [setAddress]);

  // 재시도 핸들러
  const handleRetry = useCallback(async () => {
    if (!failedStayId || isRetrying) return;
    setIsRetrying(true);
    setStayLoadError(false);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    await loadStayInfo(failedStayId);
    setIsRetrying(false);
  }, [failedStayId, isRetrying, loadStayInfo]);

  // 토스트 닫기
  const dismissToast = useCallback(() => {
    setStayLoadError(false);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    const handleUrlParameters = async () => {
      // 해시 라우터에서는 쿼리 파라미터가 location.search와 hash 부분에 모두 있을 수 있음
      let searchParams = "";

      // location.search에서 파라미터 확인
      if (location.search) {
        searchParams = location.search;
      }

      // 해시 부분에서도 쿼리 파라미터 확인 (예: #/guides/1?stayId=...)
      const hashParts = window.location.hash.split("?");
      if (hashParts.length > 1) {
        searchParams = "?" + hashParts[1];
      }

      const urlParams = new URLSearchParams(searchParams);
      const stayIdFromQuery = urlParams.get("stayId");
      const promotionUserIdFromQuery = urlParams.get("promotionUserId");
      const langFromQuery = urlParams.get("lang");

      let hasParamsToStore = false;
      let shouldLoadStay = false;
      let stayIdToLoad: string | null = null;

      if (stayIdFromQuery) {
        // 이미 이 stayId로 로드했다면 스킵
        if (hasLoadedRef.current !== stayIdFromQuery) {
          // localStorage에 저장
          savePendingStayId(stayIdFromQuery);
          shouldLoadStay = true;
          stayIdToLoad = stayIdFromQuery;
        }
        urlParams.delete("stayId");
        hasParamsToStore = true;
      }

      if (promotionUserIdFromQuery) {
        // localStorage에 promotion user ID 저장
        try {
          savePromotionUserId(promotionUserIdFromQuery, 'deeplink');
        } catch (error) {
          console.error("Failed to save promotionUserId to localStorage:", error);
        }
        urlParams.delete("promotionUserId");
        hasParamsToStore = true;
      }

      if (langFromQuery) {
        // 언어 설정 변경
        const supportedLanguages = ["en", "ko", "ja", "zh-CN", "zh-TW"];
        if (supportedLanguages.includes(langFromQuery)) {
          setLanguage(langFromQuery as SupportedLanguage);
        }
        urlParams.delete("lang");
        hasParamsToStore = true;
      }

      if (hasParamsToStore) {
        // URL에서 파라미터들 제거
        const cleanSearch = urlParams.toString();
        const cleanUrl = cleanSearch ? `?${cleanSearch}` : "";

        // 해시 라우터의 경우 해시 부분을 업데이트
        if (hashParts.length > 1) {
          const newHash = hashParts[0] + cleanUrl;
          window.history.replaceState(
            {},
            "",
            window.location.pathname + window.location.search + newHash
          );
        } else {
          // 일반적인 경우
          const newUrl = `${location.pathname}${cleanUrl}${location.hash}`;
          window.history.replaceState({}, "", newUrl);
        }
      }

      // localStorage에서 pendingStayId 확인 (URL 파라미터에서 처리되지 않은 경우)
      if (!shouldLoadStay) {
        const pendingStayId = loadPendingStayId();
        if (pendingStayId && hasLoadedRef.current !== pendingStayId) {
          // 온보딩 상태 확인 - 온보딩 중이면 useStayPreloader에서 처리하므로 여기서는 처리하지 않음
          const hasSeenOnboarding = localStorage.getItem("hasSeenOnboarding");
          
          if (hasSeenOnboarding) {
            // 온보딩이 완료된 경우에만 여기서 처리
            shouldLoadStay = true;
            stayIdToLoad = pendingStayId;
            // ⚠️ 삭제는 API 성공 후에 수행 (loadStayInfo 내부)
            console.log("📍 GlobalUrlParameterHandler: pendingStayId 처리 (온보딩 완료)", pendingStayId);
          } else {
            // 온보딩 중이면 useStayPreloader에서 처리하므로 여기서는 처리하지 않음
            console.log("📍 GlobalUrlParameterHandler: 온보딩 중이므로 pendingStayId 처리 건너뛰기", pendingStayId);
          }
        }
      }

      // stayId가 있으면 stay 정보 로드
      if (shouldLoadStay && stayIdToLoad) {
        await loadStayInfo(stayIdToLoad);
      }
    };

    handleUrlParameters();
  }, [location.search, location.pathname, location.hash, setAddress, loadStayInfo]);

  // 타이머 정리
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // 에러 상태가 아니면 아무것도 렌더링하지 않음
  if (!stayLoadError && !isRetrying) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 72,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        maxWidth: 360,
        width: 'calc(100% - 32px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 14px',
          backgroundColor: '#1f2937',
          color: '#fff',
          borderRadius: 10,
          fontSize: 13,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <span style={{ flex: 1 }}>
          {isRetrying ? getTranslation('common.loading') : getTranslation('stay.loadError')}
        </span>
        {!isRetrying && (
          <>
            <button
              onClick={handleRetry}
              style={{
                padding: '4px 12px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {getTranslation('stay.retry')}
            </button>
            <button
              onClick={dismissToast}
              style={{
                padding: '2px 6px',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                border: 'none',
                fontSize: 16,
                cursor: 'pointer',
                lineHeight: 1,
              }}
              aria-label="close"
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// 글로벌 서비스 상태 관리 컴포넌트
const GlobalServiceStatusManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const lastCheckTimeRef = useRef<number>(0);
  const MINIMUM_CHECK_INTERVAL = 30 * 1000; // 30초 최소 간격

  useEffect(() => {
    const checkServiceStatus = async () => {
      // Figma html-to-design: 캡처 URL에 figmacapture가 있으면 메인터넌스로 보내지 않음(로컬 캡처 전용)
      // HashRouter: 쿼리는 hash 안의 location.search에도 온다.
      if (
        location.search.includes("figmacapture") ||
        (typeof window !== "undefined" && window.location.hash.includes("figmacapture"))
      ) {
        return;
      }

      // 이미 메인터넌스 페이지에 있으면 체크하지 않음
      if (location.pathname === "/maintenance") {
        return;
      }

      // 중복 체크 방지
      if (isChecking) {
        return;
      }

      // 최소 간격 체크
      const now = Date.now();
      if (now - lastCheckTimeRef.current < MINIMUM_CHECK_INTERVAL) {
        return;
      }

      setIsChecking(true);
      lastCheckTimeRef.current = now;

      try {
        // 캐시된 상태 확인 (OperationTimeService의 캐시 활용)
        const status = await getOperationStatus();

        // 에러 상태이면 메인터넌스 페이지로 이동
        if (status.isError) {
          navigate("/maintenance", { replace: true });
        }
      } catch (error) {
        console.error("Service status check failed:", error);
        navigate("/maintenance", { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    // 페이지 변경 시마다 서비스 상태 확인 (하지만 캐시와 간격 제한 적용)
    checkServiceStatus();
  }, [
    location.pathname,
    location.search,
    navigate,
    isChecking,
    MINIMUM_CHECK_INTERVAL,
  ]);

  return null;
};

// 메인터넌스 페이지 컴포넌트
const MaintenancePageComponent = () => {
  const navigate = useNavigate();

  const handleServiceReady = () => {
    // 서비스 복구 시 홈으로 이동
    navigate("/", { replace: true });
  };

  return <MaintenancePage onServiceReady={handleServiceReady} />;
};

// 홈 화면 컴포넌트
const HomeComponent = () => {
  const { address } = useAddress();
  const [, setPromotionData] = useState<PromotionPopupData | null>(
    null
  );
  const [, setShowPromotionPopup] = useState(false);
  const [showHomeRecentOrderAffordance, setShowHomeRecentOrderAffordance] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Stay 정보 백그라운드 로드 (최종 안전장치)
  useStayPreloader();
  
  // 홈페이지 데이터 백그라운드 프리로딩 (최종 안전장치)
  useHomeDataPreloader();

  // 온보딩 완료 여부 체크
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem("hasSeenOnboarding");

    // 온보딩을 본 적이 없다면 온보딩 페이지로 리다이렉트
    if (!hasSeenOnboarding) {
      // Figma html-to-design: hash 쿼리(figmacapture 등)를 유지해야 캡처가 동작함
      navigate(
        { pathname: "/onboarding", search: location.search },
        { replace: true }
      );
      return;
    }
  }, [navigate, location.search]);

  // 프로모션 데이터 로드
  const loadPromotionData = useCallback(async () => {
    try {
      const currentLang = getCurrentLanguage();
      const addressId = address?.addressId;
      const promotionUserId = getPromotionUserId();

      // addressId가 없으면 프로모션 데이터 로드를 건너뜀
      if (!addressId) {
        console.debug("Address ID not available, skipping promotion data load");
        return;
      }

      // 프로모션 코드 조회 (addressId 포함)
      const promotionData = await getPromotionCode(
        currentLang,
        addressId,
        promotionUserId
      );

      if (
        promotionData &&
        promotionData.isActive &&
        !promotionData.isApplied &&
        canShowPromotionPopup(
          promotionData.startDate,
          promotionData.endDate,
          promotionData.code
        )
      ) {
        const popupData = transformPromotionData(promotionData);
        setPromotionData(popupData);
        setShowPromotionPopup(true);
      }
    } catch (error) {
      console.error("Error loading promotion data:", error);
    }
  }, [address?.addressId]);

  // 초기 프로모션 데이터 로드
  useEffect(() => {
    loadPromotionData();
  }, [loadPromotionData]);

  // 주소 변경 시 프로모션 데이터 다시 로드
  useEffect(() => {
    if (address?.addressId) {
      loadPromotionData();
    }
  }, [address?.addressId, loadPromotionData]);

  // 언어 변경 시 프로모션 데이터 다시 로드
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: SupportedLanguage }>;
      console.debug(
        "Language changed in HomeComponent, reloading promotion data:",
        customEvent.detail.language
      );

      // 현재 팝업이 열려있다면 닫기
      setShowPromotionPopup(false);
      setPromotionData(null);

      // 새 언어로 프로모션 데이터 다시 로드
      setTimeout(() => {
        loadPromotionData();
      }, 500);
    };

    // 언어 변경 이벤트 리스너 등록
    window.addEventListener(
      LANGUAGE_CHANGE_EVENT,
      handleLanguageChange as EventListener
    );

    return () => {
      // 이벤트 리스너 제거
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange as EventListener
      );
    };
  }, [loadPromotionData]);

  return (
    <>
      <Header />
      <main className="md-background pb-28">
        <LocationBanner />
        <div className="md-surface">
          <GuideBanner />
        </div>
        <div className="md-surface">
          <CategorySection />
        </div>
        <div className="md-surface">
          <CurationSection />
        </div>
        <div className="md-surface mt-2">
          <Footer />
        </div>
      </main>
      <BottomBanner isVisible={!showHomeRecentOrderAffordance} />
      <HomeRecentOrderAffordance onVisibilityChange={setShowHomeRecentOrderAffordance} />
      <NavigationBar />

      {/* 프로모션 팝업 */}
      {/* {promotionData && (
        <PromotionPopup
          promotion={promotionData}
          isVisible={showPromotionPopup}
          onClose={handleClosePromotionPopup}
        />
      )} */}
    </>
  );
};

// 앱 수준의 이벤트 핸들러 컴포넌트
const AppEventHandler = () => {
  useEffect(() => {
    // 언어 변경 이벤트 발생 시, 해당 언어의 API 캐시를 무효화
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        language: SupportedLanguage;
        timestamp: number;
      }>;
      const newLang = customEvent.detail.language;

      // 이전 언어의 캐시 무효화
      invalidateLanguageCache(newLang);
    };

    // 언어 변경 이벤트 리스너 등록
    window.addEventListener(
      LANGUAGE_CHANGE_EVENT,
      handleLanguageChange as EventListener
    );

    return () => {
      // 이벤트 리스너 제거
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange as EventListener
      );
    };
  }, []);

  return null;
};

// 앱 컴포넌트를 감싸는 라우터 컨텍스트 제공 컴포넌트
const AppWithRouter = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

// 실제 앱 컨텐츠 컴포넌트
const AppContent = () => {
  return (
    <div className="App bg-gray-50 min-h-screen">
      <div className="max-w-mobile mx-auto min-h-screen bg-white shadow-lg">
        <AnalyticsTracker />
        <ChannelTalkManager />
        <AppEventHandler />
        <GlobalServiceStatusManager />
        <FigmaMcpCaptureRunner />
        <GlobalUrlParameterHandler />
        <BackgroundPollingManager />
        <OperationTimeGuard protectedPaths={["/cart", "/checkout"]}>
          <ServiceClosingGuard>
            <NavigationAffordanceProvider>
              <Routes>
                {/* 홈 화면 */}
                <Route path="/" element={<HomeComponent />} />

                {/* 홈 화면 (GitHub Pages용 추가 경로) */}
                <Route path="/home" element={<HomeComponent />} />

                {/* 온보딩 페이지 */}
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/onboarding2" element={<OnboardingPage2 />} />
                <Route path="/onboarding3" element={<OnboardingPage3 />} />
                <Route path="/onboarding4" element={<OnboardingPage4 />} />

                {/* 메인터넌스 페이지 */}
                <Route
                  path="/maintenance"
                  element={<MaintenancePageComponent />}
                />

                {/* 이용약관 페이지 */}
                <Route path="/terms" element={<TermsOfServicePage />} />

                {/* 개인정보처리방침 페이지 */}
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* FAQ 페이지 */}
                <Route path="/faq" element={<FAQPage />} />

                {/* FAQ 섹션 페이지 */}
                <Route path="/faq/:sectionId" element={<FAQSectionPage />} />

                {/* 카테고리 상세 페이지 */}
                <Route
                  path="/categories/:categoryId"
                  element={<CategoryDetail />}
                />

                {/* 이전 라우트 유지 (하위 호환성) */}
                <Route
                  path="/categories/:categoryId/:categoryName"
                  element={<CategoryDetail />}
                />

                {/* 큐레이션 상세 페이지 */}
                <Route
                  path="/curations/:categoryId"
                  element={<CurationDetail />}
                />

                {/* 전체 레스토랑 목록 (Browse) */}
                <Route path="/browse" element={<CategoryDetail />} />
                <Route path="/restaurants" element={<CategoryDetail />} />

                {/* 가이드 페이지 */}
                <Route path="/guides" element={<GuidePage />} />

                {/* 체인점 상세 페이지 */}
                <Route
                  path="/chains/:chainId/:chainName"
                  element={<CategoryDetail />}
                />

                {/* 기프트샵 상세 페이지 */}
                <Route
                  path="/restaurant/gift-shop-1"
                  element={<GiftShopDetail />}
                />

                {/* 가게 상세 페이지 */}
                <Route
                  path="/restaurant/:restaurantId"
                  element={<RestaurantDetail />}
                />

                {/* 메뉴 상세 페이지 */}
                <Route
                  path="/restaurant/:restaurantId/menu/:menuId"
                  element={<MenuDetail />}
                />

                {/* 기프트샵 상품 상세 페이지 */}
                <Route
                  path="/shop/:shopId/product/:productId"
                  element={<ProductDetail />}
                />

                {/* 검색 지면 페이지 */}
                <Route path="/search" element={<SearchPage />} />

                {/* 장바구니 페이지 */}
                <Route path="/cart" element={<CartPage />} />

                {/* 결제 페이지 */}
                <Route path="/checkout" element={<CheckoutPage />} />

                {/* 주문 성공 페이지 */}
                <Route path="/order-success" element={<OrderSuccessPage />} />

                {/* 최근 주문 내역 페이지 */}
                <Route path="/orders" element={<OrdersPage />} />

                {/* 엑심베이 결제 콜백 페이지 */}
                <Route path="/payment-callback" element={<PaymentCallbackPage />} />

                {/* 주소 설정 페이지 */}
                <Route path="/address" element={<AddressSetupPage />} />

                {/* 주소 설정 방법 선택 페이지 */}
                <Route path="/address/method" element={<AddressMethodSelection />} />

                {/* 주소 스크린샷 업로드 페이지 */}
                <Route path="/address-screenshot" element={<AddressScreenshotUpload />} />

                {/* 가이드 상세 페이지 */}
                <Route path="/guides/:guideId" element={<GuideDetailPage />} />

                {/* 검색 결과 페이지 */}
                <Route path="/search-results" element={<SearchResultPage />} />

                {/* 프로모션 코드 페이지 */}
                <Route path="/promotion-code" element={<PromotionCodePage />} />

                {/* 404 에러 페이지 - 항상 모든 라우트의 마지막에 배치 */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </NavigationAffordanceProvider>
          </ServiceClosingGuard>
        </OperationTimeGuard>

        {/* 전역 컴포넌트 - 어디서든 접근 가능 */}
        <CartReplaceConfirm />
      </div>
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <AddressProvider>
        <AddressAffordanceProvider>
          <AppWithRouter />
        </AddressAffordanceProvider>
      </AddressProvider>
    </CartProvider>
  );
}

export default App;
